
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Auth from 'plaid-threads/Icons/ProductAuth';
import Actions from '../../../../components/docs/Actions';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Authenticate your users instantly'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Instant Auth, Instant Match, and Instant Micro-deposits' subtitle='Learn how to authenticate your users instantly' mdxType="Header" />
    <h4 {...{
      "id": "instant-auth"
    }}>{`Instant Auth`}</h4>
    <p>{`Instant Auth supports more than 9,000 financial institutions
with credential-based login. Instant Auth is the default Auth method and does not require extra configuration steps if Auth is already configured in your app. For clarity and completeness, the section below explains how to configure Instant Auth.`}</p>
    <ProductTour steps={[{
      text: 'The user launches Link...',
      image: '/assets/img/docs/auth/ia_tour/link-1-rm.png'
    }, {
      text: '...and selects the institution to link.',
      image: '/assets/img/docs/auth/ia_tour/select-institution.png'
    }, {
      text: 'They are handed off to the institution...',
      image: '/assets/img/docs/auth/ia_tour/oauth-handoff.png'
    }, {
      text: '...and log in.',
      image: '/assets/img/docs/auth/ia_tour/link-gingham.png'
    }, {
      text: 'Next, they select the account to link. (Single-account select is typically recommended for Auth.)',
      image: '/assets/img/docs/auth/ia_tour/account-select.png'
    }, {
      text: '...and their account is linked!',
      image: '/assets/img/docs/auth/ia_tour/link-success.png'
    }]} mdxType="ProductTour" />
    <p>{`You can try out the Instant Auth flow in an `}<a parentName="p" {...{
        "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100&step=4"
      }}>{`Interactive Demo`}</a>{`.`}</p>
    <h5 {...{
      "id": "configure--create-a-link_token"
    }}>{`Configure & Create a link_token`}</h5>
    <p>{`Create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the following parameters:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`products`}</inlineCode>{` array containing `}<inlineCode parentName="li">{`auth`}</inlineCode>{` – If you are using only `}<inlineCode parentName="li">{`auth`}</inlineCode>{` and no other products, `}<inlineCode parentName="li">{`auth`}</inlineCode>{` must be specified in the Products array. Other products (such as `}<inlineCode parentName="li">{`identity`}</inlineCode>{`) may be specified as well. If you are using multiple products, `}<inlineCode parentName="li">{`auth`}</inlineCode>{` is not required to be specified in the products array, but including it is recommended for the best user experience.`}</li>
    </ul>
    <SampleCode.CreateLinkToken product='auth' />
    <h5 {...{
      "id": "initialize-link-with-a-link_token"
    }}>{`Initialize Link with a link_token`}</h5>
    <p>{`After creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` product, use it to initialize Plaid Link.`}</p>
    <p>{`When the user inputs their username and password for the financial institution,
the `}<inlineCode parentName="p">{`onSuccess()`}</inlineCode>{` callback function will return a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "#App.js",
        "#App.js": true
      }}>{`Plaid.create({
  // Fetch a link_token configured for 'auth' from your app server
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token and accounts to your app server
    $.post('/exchange_public_token', {
      publicToken: public_token,
      accounts: metadata.accounts,
    });
  },
});
`}</code></pre>
    <h5 {...{
      "id": "exchange-the-public_token-and-fetch-auth-data"
    }}>{`Exchange the public_token and fetch Auth data`}</h5>
    <p>{`In your own backend server, call the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`
endpoint with the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` received in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback to obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`.
Persist the returned `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in your database in relation to the user. You will use
the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` when making requests to the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` endpoint.`}</p>
    <MDXCodeBlock type='server' title='Exchange token and fetch Auth data' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/item/public_token/exchange \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  }'

curl -X POST https://sandbox.plaid.com/auth/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "\${ACCESS_TOKEN}"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d';

try {
  // Obtain an access_token from the Link public_token
  const tokenResponse = await client.itemPublicTokenExchange({
      public_token: publicToken});
  const accessToken = tokenResponse.data.access_token;

  // Instantly fetch Auth numbers
  const request: AuthGetRequest = {
    access_token: accessToken,
  };
  const response = await client.authGet(request);
  const numbers = response.data.numbers;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d'

# Obtain an access_token from the Link public_token
token_request = ItemPublicTokenExchangeRequest(
      public_token=publicToken)
token_response = client.item_public_token_exchange(token_request)
accessToken = token_response['access_token']

# Instantly fetch Auth numbers
auth_request = AuthGetRequest(access_token=accessToken)
auth_response = client.auth_get(auth_request)
numbers = auth_response['numbers']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d';

# Obtain an access_token from the Link public_token
item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: publicToken
  }
)
token_response = client.item_public_token_exchange(
  item_public_token_exchange_request
)
accessToken = token_response.access_token

# Instantly fetch Auth numbers
auth_get_request = Plaid::AuthGetRequest.new
auth_get_request.access_token = accessToken

auth_response = client.auth_get(auth_get_request)
numbers = auth_response.numbers
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`String publicToken = "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d";

// Obtain an access_token from the Link public_token
ItemPublicTokenExchangeRequest tokenRequest = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> tokenResponse = plaidClient
  .itemPublicTokenExchange(tokenRequest)
  .execute();
if (tokenResponse.isSuccessful()) {
  String accessToken = tokenResponse.body().getAccessToken();

  // Instantly fetch Auth numbers
  AuthGetRequest authGetRequest = new AuthGetRequest()
    .accessToken(accessToken);

  Response<AuthGetResponse> authResponse = plaidClient
    .authGet(authGetRequest)
    .execute();

  if (authResponse.isSuccessful()) {
    AuthGetNumbers numbers = authResponse.body().getNumbers();
  }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// obtain an access_token from the Link public_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest(publicToken),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// Instantly fetch Auth numbers
authGetResp, _, err := client.PlaidApi.AuthGet(ctx).AuthGetRequest(
  *plaid.NewAuthGetRequest(accessToken),
).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// obtain an access_token from the Link public_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{PublicToken: publicToken})
accessToken := exchangePublicTokenResp.GetAccessToken()

// Instantly fetch Auth numbers
authGetResp, err := client.Auth.Get(ctx, &plaid.AuthGetRequest{AccessToken: accessToken})
`}</code></pre>
    </MDXCodeBlock>
  
    <p>{`Check out the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` API reference documentation to see the full
Auth request and response schema.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth response",
        "#Auth": true,
        "response": true
      }}>{`{
  "numbers": {
    "ach": [
      {
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "account": "9900009606",
        "routing": "011401533",
        "wire_routing": "021000021"
      }
    ],
    "eft": [],
    "international": [],
    "bacs": []
  },
  "accounts": [{ Object }],
  "item": { Object },
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <h4 {...{
      "id": "instant-match"
    }}>{`Instant Match`}</h4>
    <p>{`Instant Match is available for approximately 800 additional U.S. financial institutions where Instant Auth is not available. Instant Match is enabled automatically for Auth customers and is automatically
provided at supported institutions as a fallback experience when Instant Auth is not available. When using Instant Match, Plaid Link will
prompt your user to enter their account number and routing number for a depository account. Plaid will then verify the last four digits of
the user-provided account number against the account mask retrieved from the financial institution.`}</p>
    <ProductTour steps={[{
      text: 'The user launches Link...',
      image: '/assets/img/docs/auth/im_tour/link-1-rm.png'
    }, {
      text: '...and selects the institution to link.',
      image: '/assets/img/docs/auth/im_tour/select-institution.png'
    }, {
      text: 'They find their institution...',
      image: '/assets/img/docs/auth/im_tour/fi-selected.png'
    }, {
      text: '...and log in.',
      image: '/assets/img/docs/auth/im_tour/fi-login.png'
    }, {
      text: 'Next, they select the account to link. (Single-account select is typically recommended for Auth.)',
      image: '/assets/img/docs/auth/im_tour/account-select.png'
    }, {
      text: "They verify their bank's routing and account numbers...",
      image: '/assets/img/docs/auth/im_tour/im-verify-routing-account-numbers.png'
    }, {
      text: 'If those numbers match the account masks that Plaid has retrieved, the account is verified',
      image: '/assets/img/docs/auth/im_tour/link-success.png'
    }]} mdxType="ProductTour" />
    <p>{`You can try out the Instant Match flow in an `}<a parentName="p" {...{
        "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100&step=4"
      }}>{`Interactive Demo`}</a>{`. See more details in our `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing/#testing-instant-match"
      }}>{`testing guide`}</a>{`.`}</p>
    <p>{`When using Instant Match, the user can verify only a single account. Even if the Account Select properties allow selecting all or multiple accounts, the ability to select multiple depository accounts for Auth will be disabled in Link if the institution is using the Instant Match flow.`}</p>
    <h5 {...{
      "id": "configuring-in-link"
    }}>{`Configuring in Link`}</h5>
    <p>{`Instant Match will be enabled automatically if you configure the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the following parameters:`}</p>
    <ul>
      <li parentName="ul">{`add `}<inlineCode parentName="li">{`"auth"`}</inlineCode>{` to `}<inlineCode parentName="li">{`products`}</inlineCode>{` array`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`country_codes`}</inlineCode>{` set to `}<inlineCode parentName="li">{`['US']`}</inlineCode>{` (adding any other countries to the array will disable Instant Match)`}</li>
    </ul>
    <p>{`Optionally, you can disable Instant Match on a per-session basis via the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call, by setting `}<inlineCode parentName="p">{`"auth.instant_match_enabled": false`}</inlineCode>{` in the request body. If you would like to disable Instant Match automatically for all Link sessions, contact your account manager or file a support ticket via the Dashboard.`}</p>
    <MDXCodeBlock type='server' title='Instant Match Configuration' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user": {"client_user_id": "\${UNIQUE_USER_ID}"},
  "client_name": "Plaid App",
  "products": ["auth"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://sample-web-hook.com"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`link_token_create_request = Plaid::LinkTokenCreateRequest.new(
  {
    user: {
      client_user_id: "user-id",
    },
    client_name: 'Plaid Test App',
    products: ['auth'],
    country_codes: ['US'],
    language: "en",
    webhook: 'https://webhook.sample.com',
    link_customization_name: "default",
  }
)
response = client.link_token_create(
  link_token_create_request
)
link_token = response.link_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: LinkTokenCreateRequest = {
  user: { client_user_id: new Date().getTime().toString() },
  client_name: 'Plaid App',
  products: [Products.Auth],
  country_codes: [CountryCode.Us],
  language: 'en',
};
try {
  const response = await plaidClient.linkTokenCreate(request);
  const linkToken = response.data.link_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`LinkTokenCreateRequest request = new LinkTokenCreateRequest()
    .user(user)
    .clientName("very nice client name")
    .products(Arrays.asList(Products.AUTH))
    .countryCodes(Arrays.asList(CountryCode.US))
    .language("en")
    .webhook("https://example.com/webhook")
    .linkCustomizationName("default")
    .accountFilters(accountFilters);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = LinkTokenCreateRequest(
    products=[Products('auth')],
    client_name="Plaid Test App",
    country_codes=[CountryCode('US')],
    language='en',
    webhook='https://sample-webhook-uri.com',
    link_customization_name='default',
    user=LinkTokenCreateRequestUser(
        client_user_id='user-id',
    )
)

response = client.link_token_create(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`user := plaid.LinkTokenCreateRequestUser{
  ClientUserId: "user-id",
}

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetProducts([]plaid.Products{plaid.PRODUCTS_AUTH})
request.SetLinkCustomizationName("default")
request.SetWebhook("https://webhook-uri.com")
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()
if err != nil {
  panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`user := plaid.LinkTokenCreateRequestUser{ClientUserID: "user-id"}

request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Products = []plaid.Products{plaid.ProductsAuth}
request.LinkCustomizationName = plaid.String("default")
request.Webhook = plaid.String("https://webhook-uri.com")
resp, err := client.Link.CreateToken(ctx, request)
if err != nil {
    panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "handling-link-events"
    }}>{`Handling Link events`}</h5>
    <p>{`For a user who goes through the Instant Match flow, the `}<inlineCode parentName="p">{`TRANSITION_VIEW (view_name = NUMBERS)`}</inlineCode>{` event will occur after `}<inlineCode parentName="p">{`SUBMIT_CREDENTIALS`}</inlineCode>{`, and in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` will be `}<inlineCode parentName="p">{`null`}</inlineCode>{` because the user would have been verified instantly.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample Link events for Instant Match flow",
        "switcher": "false",
        "#Sample": true,
        "Link": true,
        "events": true,
        "for": true,
        "Instant": true,
        "Match": true,
        "flow": true
      }}>{`OPEN (view_name = CONSENT)
TRANSITION_VIEW (view_name = SELECT_INSTITUTION)
SEARCH_INSTITUTION
SELECT_INSTITUTION
TRANSITION_VIEW (view_name = CREDENTIAL)
SUBMIT_CREDENTIALS
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = MFA, mfa_type = code)
SUBMIT_MFA (mfa_type = code)
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = SELECT_ACCOUNT)
TRANSITION_VIEW (view_name = NUMBERS)
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = CONNECTED)
HANDOFF
onSuccess (verification_status: null)
`}</code></pre>
    <h4 {...{
      "id": "instant-micro-deposits"
    }}>{`Instant Micro-deposits`}</h4>
    <p>{`Instant Micro-deposits is the Plaid product term for our ability to authenticate any bank account in the US that is supported by RTP or FedNow. For over 80 Plaid-supported banks, Instant Micro-deposits is the fastest and highest-converting form of Auth support available. If both Instant Micro-deposits and Same-Day Micro-deposits are enabled, any user who attempts a micro-deposit with one of the over 400 eligible RTP or FedNow routing numbers will automatically experience the Instant Micro-deposits flow and be able to verify instantly.`}</p>
    <h5 {...{
      "id": "instant-micro-deposit-flow"
    }}>{`Instant Micro-deposit flow`}</h5>
    <ProductTour steps={[{
      text: 'The user launches Link...',
      image: '/assets/img/docs/auth/imd_tour/link-1-rm.png'
    }, {
      text: '...and selects the institution to link.',
      image: '/assets/img/docs/auth/imd_tour/select-institution.png'
    }, {
      text: 'They find their institution...',
      image: '/assets/img/docs/auth/imd_tour/imd-select-fi.png'
    }, {
      text: "...enter their account info...",
      image: '/assets/img/docs/auth/imd_tour/imd_enter_account_info.png'
    }, {
      text: '...their name...',
      image: '/assets/img/docs/auth/imd_tour/imd_enter_name.png'
    }, {
      text: '...and their account type.',
      image: '/assets/img/docs/auth/imd_tour/imd_enter_account_type.png'
    }, {
      text: 'The user authorizes Plaid to make a deposit, and the deposit is made immediately.',
      image: '/assets/img/docs/auth/imd_tour/imd_authorize_deposit.png'
    }, {
      text: 'The user is then prompted to enter the code associated with the deposit.',
      image: '/assets/img/docs/auth/imd_tour/imd_check_account.png'
    }, {
      text: "If the code matches the one that Plaid generated with the deposit description, the user's account is verified.",
      image: '/assets/img/docs/auth/imd_tour/link-success.png'
    }]} mdxType="ProductTour" />
    <ol>
      <li parentName="ol">{`Starting on a page in your app, the user clicks an action that opens Plaid Link.`}</li>
      <li parentName="ol">{`Inside of Plaid Link, the user enters the micro-deposit initiation flow
and provides their legal name, account and routing number.`}</li>
      <li parentName="ol">{`Plaid sends a micro-deposit to the user's account that will post within 5 seconds, and directs the user to log into their bank account to obtain the code from the micro-deposit description.`}</li>
      <li parentName="ol">{`The user enters the code from the micro-deposit description into Plaid Link.`}</li>
      <li parentName="ol">{`Upon success, Link closes with a `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` and a `}<inlineCode parentName="li">{`metadata`}</inlineCode>{` account status of `}<inlineCode parentName="li">{`manually_verified`}</inlineCode>{`.`}</li>
    </ol>
    <blockquote>
      <p parentName="blockquote">{`Plaid will not reverse the $0.01 micro-deposit credit.`}</p>
    </blockquote>
    <p>{`When these steps are done, your user's Auth data is verified and ready to fetch.`}</p>
    <p>{`You can try out the Instant Micro-deposits flow in an `}<a parentName="p" {...{
        "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100&step=7"
      }}>{`Interactive Demo`}</a>{`. `}</p>
    <h5 {...{
      "id": "configuring-in-link-1"
    }}>{`Configuring in Link`}</h5>
    <p>{`Instant Micro-deposits will be enabled if you configure the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the following parameters:`}</p>
    <ul>
      <li parentName="ul">{`Set the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array to `}<inlineCode parentName="li">{`["auth"]`}</inlineCode>{`. While in most cases additional products can be added to existing Plaid Items, Items created for micro-deposit verification cannot be used with any Plaid products other than Auth or Transfer, with the exception that approximately 30% of Items verified by Instant Micro-deposits can also be verified by `}<a parentName="li" {...{
          "href": "/docs/identity/#using-identity-match-with-micro-deposit-or-database-items"
        }}>{`Identity Match`}</a>{` and evaluated for `}<a parentName="li" {...{
          "href": "/docs/signal/signal-rules/#data-availability-limitations"
        }}>{`Signal Transaction Scores`}</a>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`country_codes`}</inlineCode>{` set to `}<inlineCode parentName="li">{`['US']`}</inlineCode>{` (adding any other countries to the array will disable Instant Micro-deposits)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`auth.instant_microdeposits_enabled`}</inlineCode>{` set to `}<inlineCode parentName="li">{`true`}</inlineCode>{`. For Plaid teams created prior to November 2023 this setting is not required; for newer teams, it must be manually configured. `}</li>
    </ul>
    <p>{`Optionally, you can disable Instant Micro-deposits on a per-session basis via the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call, by setting `}<inlineCode parentName="p">{`"auth.instant_microdeposits_enabled": false`}</inlineCode>{` in the request body. If you would like to disable Instant Micro-deposits automatically for all Link sessions, contact your account manager or file a support ticket via the Dashboard.`}</p>
    <h5 {...{
      "id": "entering-the-instant-micro-deposit-flow"
    }}>{`Entering the Instant Micro-deposit flow`}</h5>
    <p>{`Your user will enter the Instant Micro-deposit flow in the following scenarios:`}</p>
    <ul>
      <li parentName="ul">{`The user selects an eligible institution that is not enabled for Instant Auth, Instant Match, or Automated Micro-deposits.`}</li>
      <li parentName="ul">{`The Link session has `}<a parentName="li" {...{
          "href": "/docs/auth/coverage/same-day"
        }}>{`Same-Day Micro-deposits`}</a>{` enabled and the user enters an eligible routing number during the Same-Day Micro-deposits flow. In this case, the session will be "upgraded" to use Instant Micro-deposits rather than Same-Day Micro-deposits.`}</li>
    </ul>
    <h6 {...{
      "id": "instant-micro-deposit-events"
    }}>{`Instant Micro-deposit events`}</h6>
    <p>{`When a user goes through the Instant Micro-deposit flow, the session will have the `}<inlineCode parentName="p">{`TRANSITION_VIEW`}</inlineCode>{` (`}<inlineCode parentName="p">{`view_name = NUMBERS`}</inlineCode>{`) event and a `}<inlineCode parentName="p">{`TRANSITION_VIEW`}</inlineCode>{` (`}<inlineCode parentName="p">{`view_name = INSTANT_MICRODEPOSIT_AUTHORIZED`}</inlineCode>{`) event after the user authorizes Plaid to send a micro-deposit to the submitted account and routing number. In the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` will be `}<inlineCode parentName="p">{`manually_verified`}</inlineCode>{` since, unlike Same-Day Micro-deposits, Instant Micro-deposits will resolve to either a success or fail state within a single Link session.`}</p>
    <h5 {...{
      "id": "testing-the-instant-micro-deposit-flow"
    }}>{`Testing the Instant Micro-deposit flow`}</h5>
    <p>{`For credentials that can be used to test Instant Micro-deposits in Sandbox, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing/#testing-instant-micro-deposits"
      }}>{`Auth testing flows`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card link='/docs/auth/coverage/automated' linkText='View guide' title='Automated Micro-deposits' subtitle='Integrate the automated micro-deposit method' icon={<Auth mdxType="Auth" />} />
  <Actions.Card link='/docs/auth/coverage/same-day' linkText='View guide' title='Same-Day Micro-deposits' subtitle='Integrate the manual micro-deposit method' icon={<Auth mdxType="Auth" />} />
  <Actions.Card link='/docs/auth/coverage/testing' linkText='View guide' title='Testing in Sandbox' subtitle='Learn how to test each Auth method in the Sandbox' icon={<Auth mdxType="Auth" />} />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "instant-auth",
        level: 4,
        title: "Instant Auth",
      },
{
        id: "instant-match",
        level: 4,
        title: "Instant Match",
      },
{
        id: "instant-micro-deposits",
        level: 4,
        title: "Instant Micro-deposits",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
